import { cn } from '@tempo-io/sui-class-utils';
import type { ReactNode } from 'react';
import styles from './Header.module.css';
import { HeaderBreadcrumbs } from './HeaderBreadcrumbs';

type ActionWrapBreakpoint = 'sm' | 'md' | 'lg';

interface HeaderProps {
  actions?: ReactNode;
  actionsWrapBreakpoint?: ActionWrapBreakpoint;
  className?: string;
  customBreadcrumbs?: ReactNode;
  description?: string;
  disableBreadcrumbs?: boolean;
  isSubPage?: boolean;
  tabNavigation?: ReactNode;
  title?: ReactNode;
  titleActions?: ReactNode;
  titleBadge?: ReactNode;
}

const wrapBreakpointStyles: Record<ActionWrapBreakpoint, string | undefined> = {
  sm: styles.wrapSm,
  md: styles.wrapMd,
  lg: styles.wrapLg,
} as const;

export const Header = ({
  actions,
  actionsWrapBreakpoint = 'md',
  className,
  customBreadcrumbs,
  description,
  disableBreadcrumbs,
  isSubPage,
  tabNavigation,
  title,
  titleActions,
  titleBadge,
}: HeaderProps) => {
  return (
    <header className={cn('sticky top-0 z-50 w-full bg-bg-surface-base', className)}>
      <div className={cn('px-6', disableBreadcrumbs ? 'py-3' : 'pt-4 pb-1')}>
        {!disableBreadcrumbs && (
          <div data-testid="header-breadcrumbs-wrapper">
            <HeaderBreadcrumbs customBreadcrumbs={customBreadcrumbs} />
          </div>
        )}
        {(title || actions || titleActions || description) && (
          <div className={cn(styles.headerContent, wrapBreakpointStyles[actionsWrapBreakpoint])}>
            <div className={cn(styles.title, 'flex items-center gap-2 min-w-0')}>
              {title && (
                <h1
                  className={cn(
                    isSubPage ? 'text-xl' : 'text-2xl',
                    'font-medium tracking-tight truncate',
                  )}
                  data-testid="header-title"
                >
                  {title}
                </h1>
              )}
              {titleBadge && (
                <div className="flex-shrink-0" data-testid="header-title-badge">
                  {titleBadge}
                </div>
              )}
              {titleActions && (
                <div
                  className="flex items-center gap-0.5 flex-shrink-0"
                  data-testid="header-title-actions"
                >
                  {titleActions}
                </div>
              )}
            </div>
            {title && actions && (
              <div
                className={cn(styles.actions, 'flex items-center gap-2')}
                data-testid="header-actions"
              >
                {actions}
              </div>
            )}
            {description && (
              <div
                className={cn(styles.description, 'flex items-center gap-4')}
                data-testid="header-description-row"
              >
                <p
                  className="text-sm text-text-neutral-secondary flex-1 min-w-0"
                  data-testid="header-description"
                >
                  {description}
                </p>
                {!title && actions && (
                  <div className="flex items-center gap-2 shrink-0" data-testid="header-actions">
                    {actions}
                  </div>
                )}
              </div>
            )}
            {!description && !title && actions && (
              <div
                className={cn(styles.actions, 'flex items-center gap-2')}
                data-testid="header-actions"
              >
                {actions}
              </div>
            )}
          </div>
        )}
      </div>
      {tabNavigation}
    </header>
  );
};
